import { useMemo, useRef } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { weburl } from "../../axiosInstance";

const PDF_WIDTH = 1000;

const responsiveCss = `
@media (max-width: 768px) {
  #generated-resume-page .resume-body { grid-template-columns: 1fr !important; }
  #generated-resume-page .resume-main { border-right: none !important; padding: 1.25rem !important; border-bottom: 0.5px solid #e2e2e2; }
  #generated-resume-page .resume-header { padding: 1.25rem !important; }
  #generated-resume-page .resume-side { padding: 1.25rem !important; }
}
`;

const GeneratedResumeCard = ({ profile = {}, specializationOptions = [] }) => {
  const resumeRef = useRef(null);

  const specializationLabels = useMemo(() => {
    const optionMap = new Map(
      (Array.isArray(specializationOptions) ? specializationOptions : []).map((item) => [
        String(item?._id || ""),
        item?.name || "",
      ])
    );

    const raw = Array.isArray(profile.specialization)
      ? profile.specialization
      : typeof profile.specialization === "string"
      ? profile.specialization.split(",").map((s) => s.trim()).filter(Boolean)
      : [];

    return raw
      .map((item) => {
        if (typeof item === "object" && item !== null) {
          return item.name || item.label || "";
        }

        return optionMap.get(String(item)) || String(item);
      })
      .filter(Boolean);
  }, [profile.specialization, specializationOptions]);

  const workExperience = Array.isArray(profile.WorkExperience)
    ? profile.WorkExperience.filter((item) => item && (item.title || item.year || item.description))
    : [];
  const keyAchievements = Array.isArray(profile.KeyAchievements)
    ? profile.KeyAchievements
        .map((item) => (typeof item === "string" ? item : item?.title || item?.text || ""))
        .filter(Boolean)
    : [];

  const initials = (name = "") =>
    name
      .split(" ")
      .slice(0, 2)
      .map((word) => word[0])
      .join("")
      .toUpperCase() || "?";

  const experienceLabel = (value) => {
    const map = {
      0: "Intern",
      1: "1-3 Years",
      3: "3-4 Years",
      5: "5+ Years",
      10: "10+ Years",
      20: "20+ Years",
    };

    return map[value] ?? (value ? `${value} Years` : "Not specified");
  };

  const handleDownloadPDF = async () => {
    const element = resumeRef.current;
    if (!element) return;

    const button = document.getElementById("resume-pdf-btn");
    if (button) button.textContent = "Generating...";

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        // Render at desktop width so the PDF keeps the desktop layout on mobile
        windowWidth: PDF_WIDTH,
        width: PDF_WIDTH,
        scrollX: 0,
        scrollY: 0,
        onclone: (doc) => {
          const cloned = doc.getElementById("generated-resume-page");
          if (cloned) {
            cloned.style.width = `${PDF_WIDTH}px`;
            cloned.style.minWidth = `${PDF_WIDTH}px`;
            cloned.style.maxWidth = "none";
          }
          const toolbar = doc.getElementById("generated-resume-toolbar");
          if (toolbar) toolbar.style.display = "none";
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imageHeight = (canvas.height / canvas.width) * pageWidth;
      const imageData = canvas.toDataURL("image/png");

      let heightLeft = imageHeight;
      let position = 0;

      pdf.addImage(imageData, "PNG", 0, position, pageWidth, imageHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imageHeight;
        pdf.addPage();
        pdf.addImage(imageData, "PNG", 0, position, pageWidth, imageHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`${profile.username || "resume"}.pdf`);
    } catch {
      window.print();
    } finally {
      if (button) button.textContent = "Download PDF";
    }
  };

  const S = {
    wrap: { padding: "1rem 0" },
    page: {
      background: "#fff",
      border: "0.5px solid #e2e2e2",
      borderRadius: 12,
      overflow: "hidden",
      fontFamily: "system-ui, sans-serif",
    },
    header: {
      background: "#1a2744",
      color: "#fff",
      padding: "2rem",
      display: "flex",
      gap: "1.5rem",
      alignItems: "flex-start",
      flexWrap: "wrap",
    },
    avatar: {
      width: 72,
      height: 72,
      borderRadius: "50%",
      background: "rgba(255,255,255,0.15)",
      border: "2px solid rgba(255,255,255,0.3)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 24,
      fontWeight: 500,
      color: "#fff",
      flexShrink: 0,
      overflow: "hidden",
    },
    avatarImg: { width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" },
    headerInfo: { flex: 1, minWidth: 0 },
    h1: { fontSize: 22, fontWeight: 500, color: "#fff", marginBottom: 4 },
    tagline: { fontSize: 13, color: "rgba(255,255,255,0.7)", marginBottom: 12 },
    metaChips: { display: "flex", flexWrap: "wrap", gap: 8 },
    chip: {
      display: "flex",
      alignItems: "center",
      gap: 5,
      background: "rgba(255,255,255,0.1)",
      border: "0.5px solid rgba(255,255,255,0.2)",
      borderRadius: 20,
      padding: "3px 10px",
      fontSize: 12,
      color: "rgba(255,255,255,0.85)",
    },
    body: { display: "grid", gridTemplateColumns: "1fr 280px" },
    mainCol: { padding: "1.5rem 1.5rem 1.5rem 2rem", borderRight: "0.5px solid #e2e2e2" },
    sideCol: { padding: "1.5rem" },
    secTitle: {
      fontSize: 11,
      fontWeight: 500,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: "#888",
      borderBottom: "0.5px solid #e2e2e2",
      paddingBottom: 6,
      marginBottom: 12,
    },
    section: { marginBottom: "1.5rem" },
    expRole: { fontSize: 14, fontWeight: 500, color: "#1a1a1a" },
    expDur: { fontSize: 11, color: "#aaa", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 },
    expDot: { width: 6, height: 6, borderRadius: "50%", background: "#1a2744", display: "inline-block" },
    expDesc: { fontSize: 13, color: "#555", lineHeight: 1.6 },
    eduDeg: { fontSize: 14, fontWeight: 500, color: "#1a1a1a" },
    eduUni: { fontSize: 13, color: "#666" },
    eduYear: { fontSize: 11, color: "#aaa", marginTop: 2 },
    specPill: {
      background: "#e8edf7",
      color: "#1a2744",
      borderRadius: 20,
      padding: "3px 10px",
      fontSize: 12,
      display: "inline-block",
      margin: "2px 2px 2px 0",
    },
    regCard: {
      background: "#f8f9fa",
      border: "0.5px solid #e2e2e2",
      borderRadius: 8,
      padding: "10px 12px",
      marginBottom: 8,
    },
    regLabel: { fontSize: 11, color: "#aaa", marginBottom: 2 },
    regVal: { fontSize: 13, fontWeight: 500, color: "#1a1a1a" },
    infoRow: { display: "flex", alignItems: "flex-start", gap: 8, marginBottom: 10, fontSize: 13, color: "#555" },
    certItem: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "6px 0",
      borderBottom: "0.5px solid #e2e2e2",
      fontSize: 13,
      color: "#555",
    },
    achieveItem: {
      display: "flex",
      gap: 8,
      marginBottom: 8,
      fontSize: 13,
      color: "#555",
      lineHeight: 1.5,
    },
    toolbar: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "0.75rem 1.25rem",
      borderTop: "0.5px solid #e2e2e2",
      background: "#f8f9fa",
      flexWrap: "wrap",
      gap: 8,
    },
    toolbarLeft: { fontSize: 12, color: "#888" },
    btnDl: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      background: "#1a2744",
      color: "#fff",
      border: "none",
      borderRadius: 8,
      padding: "8px 16px",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
    },
  };

  return (
    <div style={S.wrap} id="generated-resume">
      <style>{responsiveCss}</style>
      <div style={S.page} ref={resumeRef} id="generated-resume-page">
        <div style={S.header} className="resume-header">
          <div style={S.avatar}>
            {profile.ProfileFile && typeof profile.ProfileFile === "string" ? (
              <img
                src={weburl + profile.ProfileFile.replace("public", "")}
                alt="Profile"
                style={S.avatarImg}
              />
            ) : (
              initials(profile.username)
            )}
          </div>
          <div style={S.headerInfo}>
            <h1 style={S.h1}>{profile.username || "Your Name"}</h1>
            <div style={S.tagline}>
              {[profile.highestMedicalDegree, specializationLabels[0], experienceLabel(profile.experience)]
                .filter(Boolean)
                .join(" | ")}
            </div>
            <div style={S.metaChips}>
              {profile.phone && <div style={S.chip}>{profile.phone}</div>}
              {profile.email && <div style={S.chip}>{profile.email}</div>}
              {profile.city && (
                <div style={S.chip}>
                  {[profile.city, profile.statename].filter(Boolean).join(", ")}
                </div>
              )}
              {profile.linkedInProfile && (
                <div style={S.chip}>{profile.linkedInProfile.replace("https://", "")}</div>
              )}
            </div>
          </div>
        </div>

        <div style={S.body} className="resume-body">
          <div style={S.mainCol} className="resume-main">
            <div style={S.section}>
              <div style={S.secTitle}>Professional Summary</div>
              <p style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>
                {profile.ProfessionalAchievements ||
                  `Dedicated and experienced medical professional${
                    profile.highestMedicalDegree ? ` with ${profile.highestMedicalDegree}` : ""
                  }${
                    specializationLabels.length
                      ? ` specialising in ${specializationLabels.slice(0, 2).join(" and ")}`
                      : ""
                  }${
                    profile.experience
                      ? ` and ${experienceLabel(profile.experience).toLowerCase()} of clinical experience`
                      : ""
                  }. Committed to delivering high-quality patient care and continuous professional development.`}
              </p>
            </div>

            <div style={S.section}>
              <div style={S.secTitle}>Education</div>
              <div>
                <div style={S.eduDeg}>{profile.highestMedicalDegree || "Degree not specified"}</div>
                <div style={S.eduUni}>{profile.institutionUniversity || profile.issuingMedicalCouncil || ""}</div>
                <div style={S.eduYear}>
                  {profile.yearOfGraduation ? `Graduated: ${profile.yearOfGraduation}` : ""}
                </div>
              </div>
            </div>

            {specializationLabels.length > 0 && (
              <div style={S.section}>
                <div style={S.secTitle}>Functional areas</div>
                <div>
                  {specializationLabels.map((item, index) => (
                    <span key={`${item}-${index}`} style={S.specPill}>
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {workExperience.length > 0 && (
              <div style={S.section}>
                <div style={S.secTitle}>Work experience</div>
                {workExperience.map((item, index) => (
                  <div key={`${item.title || "work"}-${index}`} style={{ marginBottom: "1rem" }}>
                    <div style={S.expRole}>{item.title || "Role"}</div>
                    {item.year ? (
                      <div style={S.expDur}>
                        <span style={S.expDot}></span>
                        {item.year}
                      </div>
                    ) : null}
                    {item.description ? <div style={S.expDesc}>{item.description}</div> : null}
                  </div>
                ))}
              </div>
            )}

            {(keyAchievements.length > 0 || profile.ProfessionalAchievements) && (
              <div style={S.section}>
                <div style={S.secTitle}>Key achievements</div>
                {(keyAchievements.length
                  ? keyAchievements
                  : String(profile.ProfessionalAchievements)
                      .split(/\n|\./)
                      .filter((line) => line.trim().length > 10)
                ).map((line, index) => (
                  <div key={`${line}-${index}`} style={S.achieveItem}>
                    <span style={{ color: "#1a2744", fontSize: 10, marginTop: 3 }}>+</span>
                    <span>{line.trim()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={S.sideCol} className="resume-side">
            <div style={S.section}>
              <div style={S.secTitle}>Registration & credentials</div>
              {profile.MedicalRegistrationNumber && (
                <div style={S.regCard}>
                  <div style={S.regLabel}>Registration ID</div>
                  <div style={S.regVal}>{profile.MedicalRegistrationNumber}</div>
                </div>
              )}
              {profile.issuingMedicalCouncil && (
                <div style={S.regCard}>
                  <div style={S.regLabel}>Issuing council</div>
                  <div style={S.regVal}>{profile.issuingMedicalCouncil}</div>
                </div>
              )}
              {profile.highestMedicalDegree && (
                <div style={S.regCard}>
                  <div style={S.regLabel}>Highest degree</div>
                  <div style={S.regVal}>{profile.highestMedicalDegree}</div>
                </div>
              )}
              {profile.jobLocation && (
                <div style={S.regCard}>
                  <div style={S.regLabel}>Preferred job location</div>
                  <div style={S.regVal}>{profile.jobLocation}</div>
                </div>
              )}
              {profile.employmentType && (
                <div style={S.regCard}>
                  <div style={S.regLabel}>Employment type</div>
                  <div style={S.regVal}>{profile.employmentType}</div>
                </div>
              )}
              {profile.salaryExpectation && (
                <div style={S.regCard}>
                  <div style={S.regLabel}>Salary expectation</div>
                  <div style={S.regVal}>{profile.salaryExpectation}</div>
                </div>
              )}
            </div>

            {profile.experience !== undefined && profile.experience !== "" && (
              <div style={S.section}>
                <div style={S.secTitle}>Experience</div>
                <div style={{ ...S.regCard, marginBottom: 0 }}>
                  <div style={S.regLabel}>Experience level</div>
                  <div style={S.regVal}>{experienceLabel(profile.experience)}</div>
                </div>
              </div>
            )}

            {profile.CertificationsFile && typeof profile.CertificationsFile === "string" && (
              <div style={S.section}>
                <div style={S.secTitle}>Certifications</div>
                <div style={{ ...S.certItem, borderBottom: "none" }}>
                  <a
                    href={weburl + profile.CertificationsFile.replace("public", "")}
                    target="_blank"
                    rel="noreferrer"
                    style={{ color: "#1a2744", fontSize: 13 }}
                  >
                    View Certification Document
                  </a>
                </div>
              </div>
            )}

            <div style={S.section}>
              <div style={S.secTitle}>Contact</div>
              {profile.address && (
                <div style={S.infoRow}>
                  <span>Location</span>
                  <span style={{ fontSize: 13 }}>
                    {[profile.address, profile.city, profile.statename, profile.pincode, profile.country]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              )}
              {profile.phone && (
                <div style={S.infoRow}>
                  <span>Phone</span>
                  <span style={{ fontSize: 13 }}>{profile.phone}</span>
                </div>
              )}
              {profile.email && (
                <div style={S.infoRow}>
                  <span>Email</span>
                  <span style={{ fontSize: 13 }}>{profile.email}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div style={S.toolbar} id="generated-resume-toolbar">
          <span style={S.toolbarLeft}>
            Auto-generated | Switch to "Upload Resume" to use your own file
          </span>
          <button
            id="resume-pdf-btn"
            type="button"
            style={S.btnDl}
            onClick={handleDownloadPDF}
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default GeneratedResumeCard;
